import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useLoanCenterScope', 'encompass-prospects');

export const ENCOMPASS_LINK_MISSING_TOKEN = 'ENCOMPASS_LINK_MISSING';
export const LOAN_CENTER_ACCESS_DENIED_TOKEN = 'LOAN_CENTER_ACCESS_DENIED';
export const LOAN_ACCESS_DENIED_TOKEN = 'LOAN_ACCESS_DENIED';

export type LoanCenterAccessMode =
  | 'all'
  | 'assigned'
  | 'unconfigured'
  | 'denied';

export interface LoanCenterAccessContext {
  mode: LoanCenterAccessMode;
  scopeEnabled: boolean;
  hasViewLiveLoans: boolean;
  hasViewAllLiveLoans: boolean;
  isLoanOfficer: boolean;
  isLoanOfficerAssistant: boolean;
  encompassUserIdConfigured: boolean;
  secondaryRoleTtlHours: number;
  refreshIntervalSeconds: number;
}

interface LoanCenterScopeState {
  userId: string | null;
  context: LoanCenterAccessContext | null;
  isLoading: boolean;
  error: Error | null;
}

const ACCESS_MODES = new Set<LoanCenterAccessMode>([
  'all',
  'assigned',
  'unconfigured',
  'denied'
]);

function asBoolean(value: unknown): boolean {
  return value === true;
}

function asPositiveNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

export function normalizeLoanCenterAccessContext(
  data: unknown
): LoanCenterAccessContext {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid Loan Center access response');
  }

  const raw = data as Record<string, unknown>;
  const mode = raw.mode;
  if (typeof mode !== 'string' || !ACCESS_MODES.has(mode as LoanCenterAccessMode)) {
    throw new Error('Invalid Loan Center access mode');
  }

  return {
    mode: mode as LoanCenterAccessMode,
    scopeEnabled: asBoolean(raw.scopeEnabled),
    hasViewLiveLoans: asBoolean(raw.hasViewLiveLoans),
    hasViewAllLiveLoans: asBoolean(raw.hasViewAllLiveLoans),
    isLoanOfficer: asBoolean(raw.isLoanOfficer),
    isLoanOfficerAssistant: asBoolean(raw.isLoanOfficerAssistant),
    encompassUserIdConfigured: asBoolean(raw.encompassUserIdConfigured),
    secondaryRoleTtlHours: asPositiveNumber(raw.secondaryRoleTtlHours, 24),
    refreshIntervalSeconds: asPositiveNumber(raw.refreshIntervalSeconds, 60)
  };
}

function errorText(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message ?? '');
  }
  return '';
}

export function isEncompassLinkMissingError(error: unknown): boolean {
  return errorText(error).includes(ENCOMPASS_LINK_MISSING_TOKEN);
}

export function isLoanAccessDeniedError(error: unknown): boolean {
  const message = errorText(error);
  return message.includes(LOAN_CENTER_ACCESS_DENIED_TOKEN)
    || message.includes(LOAN_ACCESS_DENIED_TOKEN);
}

export function useLoanCenterScope() {
  const { user } = useAuth();
  const userId = user?.id;
  const requestIdRef = useRef(0);
  const [state, setState] = useState<LoanCenterScopeState>({
    userId: null,
    context: null,
    isLoading: true,
    error: null
  });

  const refresh = useCallback(async (silent = false) => {
    const requestId = ++requestIdRef.current;
    if (!userId) {
      setState({
        userId: null,
        context: null,
        isLoading: false,
        error: new Error('Authentication required')
      });
      return;
    }

    if (!silent) {
      setState((current) => ({
        ...current,
        userId,
        isLoading: true,
        error: null
      }));
    }

    const { data, error } = await supabase.rpc(
      'get_loan_center_access_context'
    );
    if (requestId !== requestIdRef.current) {
      return;
    }

    if (error) {
      log.error(
        'Failed to resolve Loan Center access context',
        { userId },
        new Error(error.message)
      );
      setState({
        userId,
        context: null,
        isLoading: false,
        error: new Error(error.message)
      });
      return;
    }

    try {
      setState({
        userId,
        context: normalizeLoanCenterAccessContext(data),
        isLoading: false,
        error: null
      });
    } catch (normalizationError) {
      const error = normalizationError instanceof Error
        ? normalizationError
        : new Error(String(normalizationError));
      log.error('Invalid Loan Center access context', { userId }, error);
      setState({
        userId,
        context: null,
        isLoading: false,
        error
      });
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
    return () => {
      requestIdRef.current += 1;
    };
  }, [refresh]);

  useEffect(() => {
    if (!state.context) {
      return;
    }

    const refreshOnFocus = () => {
      if (document.visibilityState === 'visible') {
        void refresh(true);
      }
    };
    const intervalMs = Math.max(
      30,
      state.context.refreshIntervalSeconds
    ) * 1000;
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refresh(true);
      }
    }, intervalMs);

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [refresh, state.context]);

  return useMemo(() => {
    const belongsToCurrentUser = state.userId === (userId ?? null);
    const context = belongsToCurrentUser ? state.context : null;
    const error = belongsToCurrentUser ? state.error : null;
    const isLoading = !belongsToCurrentUser || state.isLoading;

    return {
      context,
      error,
      isLoading,
      mode: context?.mode ?? 'denied',
      isScoped: context?.mode === 'assigned',
      canAccess:
        context?.mode === 'all'
        || context?.mode === 'assigned',
      refresh
    };
  }, [refresh, state, userId]);
}
